/**
 * Warp — a paired teleporter (Brief 011 connectivity family).
 *
 * Two variants, selected by the `kind` ctor arg (one class, two EntityTypes):
 *   - WARP  — a portal; OVERLAPPING its mouth teleports the player.
 *   - DOOR  — same, but GATED on the enter press (press E while standing in it).
 * Pairing is by `props.linkId`: place exactly two warps/doors sharing an id; entering
 * one sends the player to the other. (Legally-distinct portals — NOT Mario pipes; the
 * down-into-pipe interaction is deliberately absent.)
 *
 * The teleport itself lives in World (it owns the player + camera): this entity only
 * DETECTS the trigger and calls `ctx.requestWarp(linkId, srcCx, srcFeetY)`, passing its
 * own mouth coords so World never picks this warp as its own destination. World's
 * post-warp cooldown swallows the destination's immediate re-trigger, so a pair never
 * ping-pongs.
 *
 * Trigger detection runs in `update` (called every step) rather than `onPlayerOverlap`
 * (called only while overlapping) so the not-overlapping frames can re-arm the rising
 * edge. Determinism: overlap + a resolved input edge only — no RNG / wall-clock.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

export type WarpKind = 'warp' | 'door';

function overlaps(a: AABB, b: AABB): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export class Warp implements Entity {
  readonly type: EntityType;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  readonly kind: WarpKind;
  readonly linkId: number;
  /** Whether the player overlapped the mouth LAST step (for the WARP rising edge). */
  private wasInside = false;

  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number, kind: WarpKind) {
    this.kind = kind;
    this.type = kind === 'door' ? EntityType.DOOR : EntityType.WARP;
    // 1×1 mouth cell (the overlap trigger); render may draw a taller door (see renderSize).
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    const raw = props?.linkId;
    const id = typeof raw === 'number' ? raw : Number(raw ?? 0);
    this.linkId = Number.isFinite(id) ? id : 0;
  }

  // NOT solid — the player walks INTO the mouth, so no solidBody().

  update(_dt: number, ctx: EntityContext): void {
    const inside = overlaps(ctx.player.body, this.body);
    const srcCx = this.body.x + this.body.w / 2;
    const srcFeetY = this.body.y + this.body.h;
    if (this.kind === 'door') {
      // Press the enter key while standing in the doorway.
      if (inside && ctx.input.up) ctx.requestWarp(this.linkId, srcCx, srcFeetY);
    } else {
      // Plain portal: fire on the rising edge of overlap (walk into it).
      if (inside && !this.wasInside) ctx.requestWarp(this.linkId, srcCx, srcFeetY);
    }
    this.wasInside = inside;
  }

  onPlayerOverlap(_ctx: EntityContext): void { /* trigger handled in update() (needs the not-inside frames) */ }

  spriteKey(): string { return this.kind === 'door' ? 'door' : 'warp'; }
  visible(): boolean { return true; }

  /** A door draws taller than its 1-tile mouth; a portal fills the cell exactly. */
  renderSize(): { w: number; h: number } {
    if (this.kind === 'door') return { w: this.body.w, h: Math.round(this.body.h * 1.4) };
    return { w: this.body.w, h: this.body.h };
  }
}
